import { mediaQuery } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

const { borderRadius, colors, spacing, typography } = themeVariables;

// Width (px) the slide image grows taller at. The design system ships no breakpoint tokens, so the
// hero owns the width it responds to. The corner switches at the same width the image height does.
const BREAKPOINT_M = 768;

const CORNER_MOBILE = `var(${borderRadius.md.mobile})`;
const CORNER_DESKTOP = `var(${borderRadius.md.desktop})`;

/**
 * The slide corner as bottom-only, for anything that sits on the image's bottom edge without being
 * inside it and so is not clipped by the wrapper. The autoplay progress bar is the one such overlay:
 * it is a sibling of the slides, and square ends would jut out past the curve.
 */
export const slideBottomCorner = css`
  border-bottom-left-radius: ${CORNER_MOBILE};
  border-bottom-right-radius: ${CORNER_MOBILE};

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      border-bottom-left-radius: ${CORNER_DESKTOP};
      border-bottom-right-radius: ${CORNER_DESKTOP};
    `,
  )}
`;

/*
 * The corner is clipped here rather than set on the image, because the heading's gradient and blur
 * are absolutely positioned siblings covering the same box. Rounding only the image would leave
 * those overlays with square corners poking past the curve.
 */
export const imageWrapper = css`
  position: relative;
  margin: 0 auto;
  overflow: hidden;
  border-radius: ${CORNER_MOBILE};

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      border-radius: ${CORNER_DESKTOP};
    `,
  )}
`;

/*
 * A hero on its own is not one of a row of cards, so it takes a softer corner: half the slide's.
 * Derived with calc rather than written out, so it stays half whatever the theme sets md to. There
 * is no token for it, which is why the halving lives here.
 */
export const imageWrapperStandalone = css`
  border-radius: calc(${CORNER_MOBILE} / 2);

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      border-radius: calc(${CORNER_DESKTOP} / 2);
    `,
  )}
`;

// Stretched, transparent overlay that makes the whole image clickable. Rendered only for the active
// slide (so there's no anchor without an href), and sits above the image but below the heading,
// whose pointer-events: none lets clicks through to it.
export const imageLink = css`
  position: absolute;
  inset: 0;
  z-index: 1;
`;

export const image = css`
  display: block;
  width: 100%;
  height: 456px;
  object-fit: cover;
  object-position: center;

  ${mediaQuery(
    { min: BREAKPOINT_M },
    css`
      height: 480px;
    `,
  )}
`;

export const heading = css`
  position: absolute;
  left: 0;
  right: 0;
  margin: 0;
  color: var(${colors.content.action});
  z-index: 2;
  line-height: var(${typography.heading.sizes.lg.lineHeight});
  /* Let clicks fall through to the image link beneath, so the whole image stays clickable. */
  pointer-events: none;

  /* The colour gradient. The blur is a separate layer (see blurLayer) so it can fade independently. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 100%;
    pointer-events: none;
    z-index: -1;
  }

  span {
    display: block;
  }
`;

/*
 * The blur, as its own layer behind the gradient (z-index: -2). The mask lives on this wrapper and
 * the backdrop-filter on its child (blurInner) on purpose: a backdrop-filter on a *masked* element
 * doesn't feather — the blur cuts off at a hard edge. Splitting them lets the blur fade smoothly
 * into the image. Sized to the heading box, so it covers exactly the gradient's region.
 */
export const blurLayer = css`
  position: absolute;
  left: 0;
  right: 0;
  height: 100%;
  z-index: -2;
  pointer-events: none;
`;

export const blurLayerTop = css`
  top: 0;
  -webkit-mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 0%, transparent 100%);
`;

export const blurLayerBottom = css`
  bottom: 0;
  -webkit-mask-image: linear-gradient(to top, #000 0%, transparent 100%);
  mask-image: linear-gradient(to top, #000 0%, transparent 100%);
`;

export const blurInner = css`
  position: absolute;
  inset: 0;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

export const headingTop = css`
  top: 0;
  padding: var(${spacing.xl}) var(${spacing.xl}) 0;
  &::before {
    top: 0;
    background: linear-gradient(to bottom, #1e1e29 0%, transparent 100%);
  }
`;

export const headingBottom = css`
  bottom: 0;
  padding: 0 var(${spacing.xl}) var(${spacing.xl});
  &::before {
    bottom: 0;
    background: linear-gradient(to top, #1e1e29 0%, transparent 100%);
  }
`;
